import type { SQLiteDatabase } from 'expo-sqlite';

import { SYSTEM_CATALOG, getSystemMeta } from '@/data/systems';
import type { PropertyRow, SystemRow } from '@/db/types';
import {
  computeProfile,
  countProfileAttributes,
  getActiveProperty,
  getSystemsForProperty,
  isProfileFieldFilled,
  PROFILE_FIELD_LABELS,
} from '@/lib/tasks';
import { dismissMessages, postMessage } from '@/lib/messages';
import { cancelPostOnboardingNudge } from '@/lib/notifications';

/**
 * Weekly "fill in one blank" prompts.
 *
 * Why this exists:
 *   - Onboarding captures the obvious stuff but the long tail (model
 *     numbers, install years on equipment, subtypes the user skipped)
 *     stays empty unless we surface it.
 *   - Big "complete your profile" prompts feel like a chore. One
 *     question a week, with a clear unlock ("picking your roof type
 *     schedules the right inspection cadence"), feels manageable.
 *
 * What it does:
 *   - Once per ISO week, posts a SINGLE inbox message to the user
 *     about ONE missing detail. The message has a `cta_route` so
 *     tapping it lands on the right edit screen immediately.
 *   - Idempotency is enforced by seed_key (`weekly-tune-{YYYY-WW}`).
 *     The first call in a given week posts; subsequent calls that
 *     same week no-op via INSERT OR IGNORE.
 *   - Caps itself: stops posting when the overall profile is ≥85%
 *     complete OR when no askable gaps remain. Resumes if a new
 *     system gets added (because that creates new gaps).
 *
 * What it deliberately does NOT do:
 *   - Push notifications. The message lands in the inbox; the user
 *     sees it on the ticker bar or when they open the bell. The push
 *     story is its own feature (see notifications.ts).
 *   - Multi-question forms. Each week is one chip-or-text answer,
 *     max. That's the whole point.
 */

interface NextGap {
  /** Title shown on the message card. Should fit on one line on phone. */
  title: string;
  /** Body shown after the user expands or hovers. Explains *why* the
   *  answer matters — earns the click. */
  body: string;
  /** Deep link route. Lands the user on the screen where they can
   *  fill the gap with the fewest taps. */
  route: string;
  /** Stable identifier of the gap the prompt targets. Encoded into the
   *  seed_key so we can sweep the inbox later and auto-dismiss the
   *  prompt the moment the user fills the underlying field. Format:
   *    'attr:<column>'       — e.g. 'attr:water_source'
   *    'sub:<systemId>'      — for system-subtype gaps */
  target: string;
}

/**
 * ISO 8601 week-of-year. We use this as the seed key suffix so each
 * calendar week gets at most one post even across DST shifts and
 * timezone changes.
 *
 * Returns "YYYY-Www" e.g. "2026-W22".
 */
function isoYearWeek(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Thursday of the current week determines the ISO year.
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/**
 * Determine the highest-leverage gap to ask about. Priority order:
 *
 *   1. Critical property attributes (ZIP, climate, roof, exterior,
 *      foundation, basement) — these affect rule scheduling broadly.
 *   2. HVAC, water source, sewer — same scope but slightly narrower
 *      impact.
 *   3. Sizing (year, sqft, bedrooms, bathrooms, stories, lot, garage).
 *   4. Identity (owner name, home name, address).
 *   5. System subtypes that haven't been picked.
 *
 * We walk the list and return the FIRST gap that's still open. The
 * ordering is the prioritization — no scoring math needed.
 */
function findNextGap(property: PropertyRow, systems: SystemRow[]): NextGap | null {
  // Weekly nudges point at /profile-setup (the Tune walkthrough) so
  // the user lands directly on the relevant card instead of having
  // to scan the full Edit my home form for the missing field.
  const editRoute = '/profile-setup';

  // Tier 1: high-leverage property attributes.
  const tier1: { field: keyof PropertyRow; route: string; bodySuffix: string }[] = [
    { field: 'zip', route: editRoute, bodySuffix: 'tunes task timing to your local frost dates.' },
    { field: 'roof_material', route: editRoute, bodySuffix: 'unlocks roof-specific inspection cadence.' },
    { field: 'exterior_material', route: editRoute, bodySuffix: 'sets the right siding seal / paint schedule.' },
    { field: 'foundation', route: editRoute, bodySuffix: 'drives crawl-walk / slab-walk reminders.' },
    { field: 'basement', route: editRoute, bodySuffix: 'turns on sump + humidity check tasks if relevant.' },
    { field: 'water_source', route: editRoute, bodySuffix: 'adds well-water testing if you\'re on a well.' },
    { field: 'sewer', route: editRoute, bodySuffix: 'adds septic pumping reminders if you\'re on septic.' },
    { field: 'hvac_type', route: editRoute, bodySuffix: 'tailors HVAC service tasks to your system.' },
    { field: 'year_band', route: editRoute, bodySuffix: 'adds old-home checks (panel, paint) where relevant.' },
    { field: 'sqft', route: editRoute, bodySuffix: 'helps estimate filter sizes and supply quantities.' },
    { field: 'lot_band', route: editRoute, bodySuffix: 'scales outdoor tasks (mowing, leaf load).' },
    { field: 'stories', route: editRoute, bodySuffix: 'flags ladder-height inspections as harder.' },
    { field: 'bedrooms', route: editRoute, bodySuffix: 'helps right-size the septic pump cadence.' },
    { field: 'bathrooms', route: editRoute, bodySuffix: 'helps schedule bath-fan + caulk checks.' },
    { field: 'garage', route: editRoute, bodySuffix: 'turns on garage-specific tasks.' },
    { field: 'name', route: editRoute, bodySuffix: 'powers the home greeting.' },
    { field: 'owner_first_name', route: editRoute, bodySuffix: 'so we can greet you by name.' },
    { field: 'address', route: editRoute, bodySuffix: 'unlocks the map on My Home + accurate climate.' },
  ];

  for (const entry of tier1) {
    const value = property[entry.field];
    if (!isProfileFieldFilled(value)) {
      const label = PROFILE_FIELD_LABELS[String(entry.field)] ?? String(entry.field);
      return {
        title: `Quick one — ${label}?`,
        body: `Filling in ${label.toLowerCase()} ${entry.bodySuffix} Takes about 10 seconds.`,
        route: entry.route,
        target: `attr:${String(entry.field)}`,
      };
    }
  }

  // Tier 2: system subtypes that haven't been picked. We pick the
  // FIRST eligible system (catalog has subtypes, user hasn't chosen)
  // and route to the walkthrough so a single tap drops them into the
  // card that asks the question.
  for (const sys of systems) {
    const meta = SYSTEM_CATALOG[sys.kind];
    if (meta?.subtypes?.length && !sys.subtype) {
      const label = getSystemMeta(sys.kind)?.label ?? sys.kind;
      return {
        title: `What kind of ${label.toLowerCase()}?`,
        body: `One tap picks the variation. We use that to choose the right tasks for this ${label.toLowerCase()} — different types need different care.`,
        route: '/profile-setup',
        target: `sub:${sys.id}`,
      };
    }
  }

  return null;
}

/**
 * Compute the same profile percentage the Home ring shows, so the
 * weekly-prompt gate agrees with what the user sees. Returns 0..100.
 */
function profilePct(property: PropertyRow, systems: SystemRow[]): number {
  const attrs = countProfileAttributes(property);

  let subtypeEligible = 0;
  let subtypesSelected = 0;
  for (const sys of systems) {
    const meta = SYSTEM_CATALOG[sys.kind];
    if (meta?.subtypes?.length) {
      subtypeEligible += 1;
      if (sys.subtype) subtypesSelected += 1;
    }
  }

  return computeProfile(property, {
    attributesFilled: attrs.filled,
    attributesTotal: attrs.total,
    subtypeEligibleSystems: subtypeEligible,
    subtypesSelected,
    hasCompletedAnyTask: false, // not part of the formula any more
  });
}

/**
 * Public entry point. Call from somewhere that runs on app focus
 * (AppGlobals). The function is internally idempotent within an ISO
 * week — calling it 100 times in the same week posts at most once.
 *
 * Returns true if a prompt was posted this call, false otherwise.
 * (The return value is purely for tests / debugging; nothing acts on it.)
 */
export async function maybePostWeeklyPrompt(
  db: SQLiteDatabase
): Promise<boolean> {
  const property = await getActiveProperty(db);
  if (!property) return false;

  const systems = await getSystemsForProperty(db, property.id);

  // Stop pestering near-complete profiles. 85% (same scale as the Home
  // ring) is the soft cap above which remaining gaps are
  // diminishing-returns territory.
  const pct = profilePct(property, systems);
  if (pct >= 85) {
    // Profile is sufficiently complete — also kill any pending 24h
    // post-onboarding push so users who race past onboarding don't
    // get told to "answer a couple of chip questions" later.
    void cancelPostOnboardingNudge(property.id);
    return false;
  }

  const gap = findNextGap(property, systems);
  if (!gap) {
    // No gaps left — same logic as the 85% gate: cancel the dangling
    // 24h push since there's nothing meaningful for it to nudge.
    void cancelPostOnboardingNudge(property.id);
    return false;
  }

  // Idempotent per-week-AND-home-AND-target: the property id is part of
  // the key because seed_key is UNIQUE — without it, two homes missing
  // the same field would collide and the second home's prompt would be
  // silently swallowed by INSERT OR IGNORE. Encoding `target` means a
  // follow-up post for the same gap in the same week is still a no-op
  // (the cap remains one prompt per ISO week per home).
  const seedKey = `weekly-tune-${isoYearWeek(new Date())}:${property.id}::${gap.target}`;
  await postMessage(db, {
    tone: 'info',
    title: gap.title,
    body: gap.body,
    seedKey,
    propertyId: property.id,
    ctaRoute: gap.route,
    // Inbox-only. The top-of-page ticker is reserved for important
    // home/account alerts (welcome message, urgent maintenance,
    // account warnings). Weekly tune nudges live in the bell — opening
    // it surfaces them deliberately, never ambiently.
    tickerHidden: true,
  });
  return true;
}

/**
 * Sweep the inbox for weekly-tune messages whose target gap is now
 * filled, and soft-delete them. Called on app focus + after any
 * profile-setup commit so a user who answers the prompt sees the
 * stale entry disappear instead of staring at "Quick one — Roof
 * material?" after they just picked Metal.
 *
 * The target is encoded in the seed_key (see maybePostWeeklyPrompt
 * for the format). Messages whose seed_key we can't parse are left
 * alone — better to leave a confusing entry than to dismiss something
 * we didn't intend.
 */
export async function dismissResolvedWeeklyPrompts(
  db: SQLiteDatabase
): Promise<number> {
  const property = await getActiveProperty(db);
  if (!property) return 0;
  const systems = await getSystemsForProperty(db, property.id);

  // Pull weekly-tune-* messages that are still alive. The seed_key
  // prefix narrows to our own seeds so we never touch anyone else's.
  // Scope to the ACTIVE property (or legacy rows with no property_id).
  // Resolving another home's prompt against this home's field values
  // would dismiss prompts that are still genuinely open over there.
  const rows = await db.getAllAsync<{ id: string; seed_key: string | null }>(
    `SELECT id, seed_key FROM message
       WHERE deleted_at IS NULL
         AND seed_key LIKE 'weekly-tune-%'
         AND (property_id IS NULL OR property_id = ?)`,
    property.id
  );
  if (rows.length === 0) return 0;

  const subtypeBySystemId = new Map<string, string | null>();
  for (const sys of systems) subtypeBySystemId.set(sys.id, sys.subtype);

  const toDismiss: string[] = [];
  for (const row of rows) {
    if (!row.seed_key) continue;
    const targetIdx = row.seed_key.indexOf('::');
    if (targetIdx < 0) continue;
    const target = row.seed_key.slice(targetIdx + 2);

    if (target.startsWith('attr:')) {
      const field = target.slice('attr:'.length) as keyof PropertyRow;
      if (isProfileFieldFilled(property[field])) toDismiss.push(row.id);
      continue;
    }
    if (target.startsWith('sub:')) {
      const systemId = target.slice('sub:'.length);
      const subtype = subtypeBySystemId.get(systemId);
      // Subtype filled → gap resolved. System deleted → also resolved
      // (the user removed it; the prompt no longer applies).
      if (subtype || !subtypeBySystemId.has(systemId)) toDismiss.push(row.id);
      continue;
    }
  }
  if (toDismiss.length === 0) return 0;

  // Soft-delete via dismissMessages so the cache refreshes and the
  // ticker / inbox UI re-render with the resolved entries gone.
  await dismissMessages(db, toDismiss);
  return toDismiss.length;
}
